/**
 * The purchase-trip preview (P2-BR2, BR-13).
 *
 * A partner buying five goats at a haat pays for the goats, then a truck, then
 * hasil, then the dalal's commission. Each animal's cost is its own price plus
 * a share of that day's overheads, split in proportion to what it cost.
 *
 * The preview shows those shares *before* the trip is submitted, because it is
 * the arithmetic the partners will check by hand the first time they see it,
 * and finding out after the fact that the app split it differently from how
 * they expected is exactly how trust in the numbers goes.
 *
 * It uses `allocateProportionally` from `@agrofarm/shared` — the same function
 * the server uses. Not a reimplementation that happens to agree: the same code,
 * so it cannot drift. TC-P2-T13-2 asserts the preview matches what the server
 * computes for the same input.
 */

import { allocateProportionally, toMoney, toMoneyString, type MoneyValue } from '@agrofarm/shared';

export interface TripLine {
  /** Blank until the server assigns one; the preview shows a position. */
  label: string;
  purchasePrice: string;
  tripShare: string;
  /** purchasePrice + tripShare — what this animal actually cost (BR-13). */
  totalCost: string;
}

export interface TripPreview {
  lines: TripLine[];
  animalsTotal: string;
  tripCostTotal: string;
  grandTotal: string;
}

const sum = (amounts: readonly string[]): MoneyValue =>
  amounts.reduce<MoneyValue>((total, amount) => total.plus(toMoney(amount || '0')), toMoney('0'));

/**
 * What the trip will cost, and what each animal will carry.
 *
 * Returns an empty preview rather than throwing when there are no animals yet:
 * the wizard calls this on every keystroke, and a half-filled form is the
 * normal state rather than an error.
 */
export function previewTrip(
  animals: readonly { purchasePrice: string; label?: string }[],
  costs: readonly { amount: string }[],
): TripPreview {
  const prices = animals.map((animal) => animal.purchasePrice || '0');
  const tripCostTotal = sum(costs.map((cost) => cost.amount));
  const animalsTotal = sum(prices);

  if (animals.length === 0) {
    return {
      lines: [],
      animalsTotal: '0.00',
      tripCostTotal: toMoneyString(tripCostTotal),
      grandTotal: toMoneyString(tripCostTotal),
    };
  }

  /*
   * The shared allocator guarantees Σ shares == the trip total exactly, with
   * the rounding remainder absorbed by the last animal (MONEY-5). Three goats
   * splitting ৳1,000 take ৳333.33, ৳333.33 and ৳333.34 — without that, one
   * paisa of the farm's money would belong to nobody.
   */
  const shares = allocateProportionally(tripCostTotal, prices);

  const lines = animals.map((animal, index) => {
    const price = toMoney(prices[index] ?? '0');
    const share = shares[index] ?? toMoney('0');

    return {
      label: animal.label ?? `#${String(index + 1)}`,
      purchasePrice: toMoneyString(price),
      tripShare: toMoneyString(share),
      totalCost: toMoneyString(price.plus(share)),
    };
  });

  return {
    lines,
    animalsTotal: toMoneyString(animalsTotal),
    tripCostTotal: toMoneyString(tripCostTotal),
    grandTotal: toMoneyString(animalsTotal.plus(tripCostTotal)),
  };
}
